import { View } from 'react-native'
import { Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { createStyle } from '../../../../contexts/global-styles.provider'
import { useCurrency } from '../../payment/payment.utils'
import { NodeTierDetail } from '../../../../generated/graphql'

interface TierCostRowProps {
  label: string
  value: number
  isTotal?: boolean
}

export const TierCostRow = ({ label, value, isTotal }: TierCostRowProps) => {
  const currency = useCurrency(value)
  const styles = useStyle()
  return (
    <Text
      variant="bodySmall"
      style={isTotal ? styles.tierTotal : styles.tierDetail}
    >
      {label}: {currency}
    </Text>
  )
}

interface ExtraSeatsRowProps {
  extraSeats: number
  extraSeatPrice: number
  total: number
}

export const ExtraSeatsRow = ({
  extraSeats,
  extraSeatPrice,
  total,
}: ExtraSeatsRowProps) => {
  const seatPriceCurrency = useCurrency(extraSeatPrice)
  const totalCurrency = useCurrency(total)
  const { t } = useTranslation('billing')
  const styles = useStyle()
  return (
    <Text variant="bodySmall" style={styles.tierDetail}>
      {t('extra-seats')}: {extraSeats} × {seatPriceCurrency} = {totalCurrency}
    </Text>
  )
}

interface TierItemTier
  extends Pick<
    NodeTierDetail,
    'nodeName' | 'tierName' | 'seats' | 'pricing' | 'basePrice'
  > {
  activeUsers?: number
  extraSeats?: number
  extraSeatPrice?: number
  totalTierCost?: number
}

interface TierItemProps {
  tier: TierItemTier
}

export const TierItem = ({ tier }: TierItemProps) => {
  const {
    seats,
    activeUsers,
    pricing,
    extraSeats = 0,
    extraSeatPrice = 0,
    basePrice,
    totalTierCost,
  } = tier
  const { t } = useTranslation('billing')
  const { t: tTiers } = useTranslation('tiers')
  const styles = useStyle()
  const translatedTierName = tTiers(`tier-name.${tier.tierName}`, tier.tierName)

  // Calculate total tier cost: base price + extra seats cost
  // totalTierCost comes from next-month, pricing comes from billing
  const calculatedTotalTierCost = totalTierCost ?? pricing

  return (
    <View style={styles.tierItem}>
      <Text variant="bodyMedium" style={styles.tierName}>
        {tier.nodeName} ({translatedTierName})
      </Text>
      <Text variant="bodySmall" style={styles.tierDetail}>
        {t('seats')}: {seats}
        {activeUsers !== undefined && ` | ${t('active-users')}: ${activeUsers}`}
      </Text>
      <TierCostRow label={t('base-price')} value={basePrice || pricing} />
      {extraSeats > 0 && extraSeatPrice > 0 && (
        <ExtraSeatsRow
          extraSeats={extraSeats}
          extraSeatPrice={extraSeatPrice}
          total={extraSeats * extraSeatPrice}
        />
      )}
      <TierCostRow
        label={t('tier-total')}
        value={calculatedTotalTierCost}
        isTotal
      />
    </View>
  )
}

const useStyle = createStyle(({ theme }) => ({
  tierDetail: {
    color: theme.colors.onSurfaceVariant,
  },
  tierTotal: {
    fontWeight: '600',
    marginTop: theme.spacing(0.5),
    color: theme.colors.primary,
  },
  tierItem: {
    padding: theme.spacing(2),
    backgroundColor: theme.colors.surfaceVariant,
    borderRadius: 8,
    gap: theme.spacing(0.5),
  },
  tierName: {
    fontWeight: '600',
    marginBottom: theme.spacing(0.5),
  },
}))
